import { useState, useCallback, useEffect } from 'react'

import {
  SearchBar,
  Button,
  CreateTableNew as CreateTable,
  GeneralMessage,      // Replacement
  useGeneralMessage    // Replacement
} from '../../../../common/APICommon';

import axios from '../../../../api/apiClient';
import useUser from '../../../../hooks/useUser';
import { WorkflowStatus } from '../../../../utils/enums';
import { ConfirmationModal } from "../../../../common/Modal";
import { token, domain } from '../../../Security';

import ModeEditIcon from '@mui/icons-material/ModeEdit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import { LoadingSpinner } from '../../../../common/Loading';
import { AddUpdateModal } from './forms/AddUpdateModal';
import { MRFDetailsModal } from './forms/MRFDetailsModal';
import { TableStatusSelect } from '../../../../common/Select';


export const RequesterMain = () => {
  const [loading, setLoading] = useState(false);
  const user = useUser();

  // Initialize General Message Hook
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  // Modal Confirmation Message State
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [selectedData, setSelectedData] = useState(null);
  const [data, setData] = useState([]);
  const [form, setForm] = useState({
    requesterId: "",
    positionId: "",
    departmentId: "",
    numberOfVacancies: "",
    requesterNotes: "",
    qualification: "",
    status: "",
    targetStartDate: null,
    approvedDate: null,
    requestedDate: null,
    completedDate: null,
    isActive: true,
  });

  //#region -------- Search --------
  const [filteredData, setFilteredData] = useState([]);
  const handleFilter = (value) => {
    setCurrentPage(1);

    const lowercaseValue = value.toLowerCase();

    const res = filteredData.filter((f) => {
      return (
        f?.requester.fullName?.toLowerCase()?.includes(lowercaseValue) ||
        f?.position.description?.toString()?.toLowerCase()?.startsWith(lowercaseValue) ||
        f?.department.description?.toString()?.toLowerCase()?.startsWith(lowercaseValue)
      )
    });

    setData(res);
  };
  //#endregion -------- Search --------

  //#region -------- Table --------
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const headers = [
    'Requested Position',
    'Department',
    'Requester',
    'Processor',
    'Requested Date',
    'Processing Date',
    'Cancelled Date',
    'Number Of Vacancies',
    'Candidates',
    'Is Active?',
    'Status',
    'Actions'
  ]
  const accessors = [
    'position.description',
    'department.description',
    'requester.fullName',
    'processor.fullName',
    'requestedDate',
    'processingDate',
    'cancelledDate',
    'numberOfVacancies',
    'candidates.length',
    'isActive',
    'status',
    'actions'
  ]

  const updateHandleClick = (data) => {
    setSelectedData(data);
    openUpdateForm()
  }

  const viewHandleClick = (data) => {
    setSelectedData(data);
    openDetailsForm()
  }

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <ModeEditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: updateHandleClick,
      visible: true,
    },
    {
      name: "View",
      tooltip: "view",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-sky-500 hover:bg-sky-600 flex items-center",
      function: viewHandleClick,
      visible: true,
    },
  ];

  const customCellRenderer = (row, accessor) => {
    if (accessor === "status") {
      const currentStatus = row.status;
      const hasCandidates = row.candidates && row.candidates.length > 0;

      // Define business logic for allowed options
      const allOptions = [
        WorkflowStatus.PENDING,
        WorkflowStatus.PROCESSING,
        WorkflowStatus.COMPLETED,
        WorkflowStatus.CANCELLED,
        WorkflowStatus.ONBOARDING
      ];

      let allowedOptions = hasCandidates
        ? allOptions.filter(opt => opt !== WorkflowStatus.PENDING)
        : allOptions;

      if (currentStatus !== WorkflowStatus.COMPLETED) {
        allowedOptions = allowedOptions.filter(opt => opt !== WorkflowStatus.ONBOARDING);
      }

      // Ensure current status is in the list
      if (!allowedOptions.includes(currentStatus)) {
        const completedIndex = allowedOptions.indexOf(WorkflowStatus.COMPLETED);
        if (completedIndex !== -1) {
          allowedOptions.splice(completedIndex + 1, 0, currentStatus);
        } else {
          allowedOptions.push(currentStatus);
        }
      }

      return (
        <TableStatusSelect
          value={currentStatus}
          options={allowedOptions}
          onChange={(newValue) => handleStatusUpdate(row, newValue)}
          disabled={currentStatus === WorkflowStatus.ONBOARDING}
        />
      );
    }
    return null;
  };
  //#endregion -------- Table --------

  //#region -------- Add Request Modal --------
  const [showAddForm, setShowAddForm] = useState(false);
  const openAddForm = () => {
    setShowAddForm(true);
  }
  const closeAddForm = () => {
    setShowAddForm(false);
    resetLocalVariables();
  }
  const handleAddRequest = async (formData) => {
    setLoading(true);

    const payload = {
      ...formData,
      requestedDate: formData.requestedDate || new Date().toISOString(),
      status: WorkflowStatus.PENDING,
      isActive: true,
    };

    const apiUrl = domain + "/api/MRF";

    try {
      await axios.post(apiUrl, payload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      await fetchData();
      showGeneralMessage('Data added successfully', true);
    } catch (error) {
      showGeneralMessage(error.response?.data?.message || "An unexpected error occurred.", false);
    } finally {
      setLoading(false);
    }
  };
  //#endregion -------- END Add Request Modal --------

  //#region -------- Update Request Modal --------
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const openUpdateForm = () => {
    setShowUpdateForm(true);
  }
  const closeUpdateForm = () => {
    setShowUpdateForm(false);
    resetLocalVariables();
  }
  const handleUpdateRequest = async (formData) => {
    const data = {
      positionId: formData.positionId,
      departmentId: formData.departmentId,
      numberOfVacancies: formData.numberOfVacancies,
      qualification: formData.qualification,
      requesterNotes: formData.requesterNotes,
      targetStartDate: formData.targetStartDate,
    };

    const apiUrl = domain + `/api/MRF/requester-update/${selectedData.id}`;

    try {
      setLoading(true);
      await axios.put(apiUrl, data, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      await fetchData();
      showGeneralMessage('Request has been updated.', true);
    } catch (error) {
      showGeneralMessage("An unexpected error occurred.", false);
    } finally {
      setLoading(false);
    }
  };
  //#endregion -------- END Update Request Modal --------

  //#region ----------- Update Global Status Modal --------
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({ id: null, newStatus: "", rowData: null });
  const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);

  const handleStatusUpdate = (row, newStatus) => {
    if (row.status === newStatus) return;

    if (newStatus === WorkflowStatus.ONBOARDING) {
      setConfirmationMessage("Are you sure you want to move the candidate(s) to OnBoarding?");
    } else {
      setConfirmationMessage(`Are you sure you want to change this request to "${newStatus.toUpperCase()}"?`);
    }

    setStatusUpdateData({ id: row.id, newStatus, rowData: row });
    setShowStatusConfirmation(true);
  };

  const confirmStatusUpdate = async () => {
    const { id, newStatus } = statusUpdateData;
    const apiUrl = domain + `/api/MRF/update-status/${id}`;

    try {
      setLoadingStatusUpdate(true);

      const payload = {
        status: newStatus,
        cancelledDate: newStatus === WorkflowStatus.CANCELLED ? new Date().toISOString() : null,
        completedDate: newStatus === WorkflowStatus.COMPLETED ? new Date().toISOString() : null,
      };
      console.log({ payload })

      await axios.put(apiUrl, payload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const updateState = prev => prev.map(item => item.id === id ? { ...item, ...payload } : item);
      setData(updateState);
      setFilteredData(updateState);

      showGeneralMessage(`Status updated to ${newStatus} successfully!`, true);
    } catch (error) {
      showGeneralMessage("Failed to update status.", false);
    } finally {
      setLoadingStatusUpdate(false);
      setShowStatusConfirmation(false);
    }
  };
  //#endregion ----------- Update Global Status Modal --------

  //#region -------- Details Modal --------
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const openDetailsForm = () => {
    setShowDetailsForm(true);
  }
  const closeDetailsForm = () => {
    setShowDetailsForm(false);
    resetLocalVariables();
  }
  //#endregion -------- END Details Modal --------

  //#region ---------Cancel Status Modal --------
  const handleCancelRequest = async () => {
    const data = {
      cancelledDate: new Date().toISOString(),
      status: WorkflowStatus.CANCELLED,
    };

    const apiUrl = domain + `/api/MRF/cancel-request-status/${selectedData.id}`;

    try {
      setLoading(true);
      await axios.put(apiUrl, data, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      await fetchData();
      showGeneralMessage('Request has been cancelled.', true);
    } catch (error) {
      showGeneralMessage("An unexpected error occurred.", false);
    } finally {
      setLoading(false);
    }
  };
  //#endregion -------- END CanCelModal Modal --------

  //#region ------- Complete Request --------
  const handleCompleteRequest = async () => {
    const data = {
      completedDate: new Date().toISOString(),
      status: WorkflowStatus.COMPLETED,
    };

    const apiUrl = domain + `/api/MRF/complete-request-status/${selectedData.id}`;

    try {
      setLoading(true);
      await axios.put(apiUrl, data, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      await fetchData();
      showGeneralMessage('Request has been completed.', true);
    } catch (error) {
      showGeneralMessage("An unexpected error occurred.", false);
    } finally {
      setLoading(false);
    }
  };
  //#endregion -------- END Update Request --------

  //#region ------------- Fetch Data ---------
  const fetchData = useCallback(async () => {
    const apiUrl = domain + "/api/MRF";
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      setData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData()
  }, [fetchData])
  //#endregion --------END Fetch Data ---------

  //#region ----------- **NEW :: Data Updater -------------
  const updateMRFInState = (updatedMRF) => {
    setData(prevData => prevData.map(item => item.id === updatedMRF.id ? updatedMRF : item));
    setFilteredData(prevData => prevData.map(item => item.id === updatedMRF.id ? updatedMRF : item));
  };

  const updateCandidatesInMRF = (mrfId, updatedCandidates) => {
    const update = prevData => prevData.map(item => item.id === mrfId ? { ...item, candidates: updatedCandidates } : item);
    setData(update); setFilteredData(update);

    if (selectedData && selectedData.id === mrfId) {
      setSelectedData(prev => ({ ...prev, candidates: updatedCandidates }));
    }
  };

  const updateCandidateInMRF = (mrfId, candidateId, updatedCandidate) => {
    const update = prevData => prevData.map(item =>
      item.id === mrfId ? {
        ...item,
        candidates: item.candidates?.map(candidate => candidate.id === candidateId ? updatedCandidate : candidate)
      } : item
    );
    setData(update); setFilteredData(update);

    if (selectedData && selectedData.id === mrfId) {
      setSelectedData(prev => ({
        ...prev,
        candidates: prev.candidates?.map(candidate => candidate.id === candidateId ? updatedCandidate : candidate)
      }));
    }
  };
  //#endregion ------------end Data Updater ----------------

  const resetLocalVariables = () => {
    setSelectedData(null);
    setForm([]);
  }

  return (
    <>
      <div>
        <SearchBar handleFilter={handleFilter} />

        <Button icon={<SendIcon />}
          name={"Request"}
          className={"bg-blue-500 hover:bg-blue-600 border-none cursor-pointer mb-2"}
          onClick={openAddForm}
        />

        <CreateTable
          tableName={"MRF"}
          headers={headers}
          accessors={accessors}
          data={data}
          actions={actions}
          customCellRenderer={customCellRenderer}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={data?.length}
        />

        <ConfirmationModal
          visible={showStatusConfirmation}
          onClose={
            () => {
              setShowStatusConfirmation(false)
            }
          }
          onConfirm={confirmStatusUpdate}
          title="Confirm Status Change"
          // message={`Are you sure you want to change this request to "${statusUpdateData.newStatus.toUpperCase()}"?`}
          message={confirmationMessage}
          confirmText="Yes, Update"
          type={statusUpdateData.newStatus === 'cancelled' ? 'danger' : 'info'}
          isLoading={loadingStatusUpdate}
        />

        <AddUpdateModal
          visible={showAddForm}
          onClose={closeAddForm}
          onSave={handleAddRequest}
          modalName={"MRF Form"}
          userData={user}
          form={form} setForm={setForm}
          showGeneralMessage={showGeneralMessage} // Pass prop
        />

        <AddUpdateModal
          visible={showUpdateForm}
          onClose={closeUpdateForm}
          onSave={handleUpdateRequest}
          modalName={"Edit MRF Form"}
          userData={user}
          form={form} setForm={setForm}
          selectedData={selectedData}
          showGeneralMessage={showGeneralMessage} // Pass prop
        />

        <MRFDetailsModal
          visible={showDetailsForm}
          onClose={closeDetailsForm}
          onCancel={handleCancelRequest}
          onComplete={handleCompleteRequest}
          modalName={"MRF Details Form"}
          data={selectedData} setData={setData}
          form={form} setForm={setForm}
          onUpdateCandidates={updateCandidatesInMRF}
          onUpdateCandidate={updateCandidateInMRF}
          onUpdateMRF={updateMRFInState}
          showGeneralMessage={showGeneralMessage} // Pass prop
        />
      </div>

      <LoadingSpinner loading={loading || loadingStatusUpdate} />

      {/* Global Message Integration */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />
    </>
  )
}